import type { EChartsOption } from 'echarts';

import { FONT_STACK_SUISSE_INTL } from '~/components/brand/brandTokens';

import type { Column } from './ReportTable';
import { addDays, divide, numeric, scalar, todaySydney } from './reportModel';

/**
 * Tableau opens Date Selection on the latest complete day (20 Oct on 21 Oct), not today, so a part day
 * is never the default. The source loads part of today during the day, so today would differ from Tableau
 * on any year-to-date or daily cadence.
 */
export const defaultBlogDate = (now = new Date()) => addDays(todaySydney(now), -1);

export const SESSION_COLUMNS: Array<Column> = [
  { key: 'sessions', label: 'No. of sessions', numeric: true },
  { key: 'sessions_with_orders', label: 'Sessions w/ orders', numeric: true },
  { key: 'conversion', label: 'Conversion rate', numeric: true, percentage: true },
  { key: 'ttv', label: 'TTV BR (AUD)', numeric: true, money: true },
  { key: 'margin', label: 'Margin BR (AUD)', numeric: true, money: true },
];
/** Measures that add up. With no matching rows they are 0; ratios then have no value. */
export const ADDITIVE_KEYS: ReadonlySet<string> = new Set([
  'sessions',
  'sessions_with_orders',
  'ttv',
  'margin',
  'page_views',
  'landing_pages',
  'bounces',
  'bounce_rows',
]);
/** The page table, in screen order. The CSV has these columns first, then the KPI measures. */
export const PAGE_TABLE_COLUMNS: Array<Column> = [
  { key: 'page_path', label: 'Page path' },
  { key: 'page_views', label: 'Count of views', numeric: true },
  { key: 'sessions', label: 'Count of sessions', numeric: true },
  { key: 'landing_pages', label: 'Landing pages', numeric: true },
  { key: 'bounce_rows', label: 'Bounces on landing pages', numeric: true },
  { key: 'table_bounce_rate', label: 'Bounce rate', numeric: true, percentage: true },
];
export const PAGE_CSV_EXTRA_COLUMNS: Array<Column> = [
  { key: 'views_per_session', label: 'Page views per session', numeric: true, decimals: 3 },
  { key: 'bounces', label: 'Bounces (distinct events)', numeric: true },
  { key: 'bounce_rate', label: 'Bounce rate (distinct events)', numeric: true, percentage: true },
];
export const VIEW_COLUMNS: Array<Column> = [
  { key: 'page_views', label: 'No. of page views', numeric: true },
  { key: 'views_per_session', label: 'Page views per session', numeric: true, decimals: 3 },
  { key: 'landing_pages', label: 'No. of landing pages', numeric: true },
  { key: 'bounces', label: 'Bounces', numeric: true },
  { key: 'bounce_rate', label: 'Bounce rate', numeric: true, percentage: true },
];
export const METRIC_KEYS: Record<string, string> = {
  Sessions: 'sessions',
  'Sessions with Orders': 'sessions_with_orders',
  'Conversion Rate': 'conversion',
  'TTV BR': 'ttv',
  'Margin BR': 'margin',
};
export function blogMetrics(row: Record<string, unknown> = {}): Record<string, unknown> {
  return {
    ...row,
    conversion: divide(numeric(row.sessions_with_orders), numeric(row.sessions)),
    views_per_session: divide(numeric(row.page_views), numeric(row.sessions)),
    bounce_rate: divide(numeric(row.bounces), numeric(row.landing_pages)),
    table_bounce_rate: divide(numeric(row.bounce_rows), numeric(row.landing_pages)),
  };
}
export function blogChart(
  rows: Array<Record<string, unknown>>,
  dimension: string,
  metric: string,
  dark: boolean,
  bars = false,
): EChartsOption {
  const percentage = metric === 'conversion' || metric === 'bounce_rate';
  const foreground = dark ? '#F9F7F1' : '#888';
  return {
    animation: false,
    textStyle: { fontFamily: FONT_STACK_SUISSE_INTL, color: foreground },
    grid: { left: bars ? 60 : 16, right: 18, top: 12, bottom: bars ? 160 : 36, containLabel: !bars },
    tooltip: {
      trigger: 'axis',
      confine: true,
      renderMode: 'richText',
      valueFormatter: (value) =>
        value === null
          ? 'Unknown'
          : percentage
            ? `${(Number(value) * 100).toFixed(2)}%`
            : Number(value).toLocaleString('en-AU'),
    },
    xAxis: {
      type: bars ? 'category' : 'time',
      data: bars ? rows.map((row) => String(scalar(row[dimension]) ?? '(Null)')) : undefined,
      axisLabel: { color: foreground, rotate: bars ? 90 : 0, interval: bars ? 0 : 'auto' },
      axisLine: { lineStyle: { color: '#eeeeee' } },
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: '#f0f0f0' } },
      axisLabel: {
        color: foreground,
        formatter: percentage
          ? (value: number) => `${(value * 100).toFixed(1)}%`
          : (value: number) => `${value / 1000}K`,
      },
    },
    series: [
      {
        name: metric,
        type: bars ? 'bar' : 'line',
        data: bars
          ? rows.map((row) => numeric(row[metric]))
          : rows.map((row) => [String(scalar(row[dimension])), numeric(row[metric])]),
        connectNulls: false,
        showSymbol: false,
        barMaxWidth: 36,
        itemStyle: { color: dark ? '#d8dae0' : '#363a45' },
        lineStyle: { color: dark ? '#d8dae0' : '#363a45', width: 3 },
      },
    ],
    aria: { enabled: true, description: `${metric} by ${dimension}. Unknown values remain gaps.` },
  };
}

/** A settled response is usable only when every card needed by this view is complete. */
export function blogResultReady(
  cards: Array<{ cardId: string; status: string; truncated?: boolean }>,
  report: string,
  settled: boolean,
): boolean {
  const required =
    report === 'views'
      ? ['views_current', 'views_previous', 'views_pages']
      : [
          'sessions_current',
          'sessions_previous',
          'sessions_trend',
          'sessions_channel',
          'sessions_country',
          'sessions_history',
        ];
  return (
    settled &&
    required.every((id) =>
      cards.some((card) => card.cardId === id && ['ready', 'empty'].includes(card.status) && !card.truncated),
    )
  );
}
